import React, { useState } from "react";
import {
  Button, Chip, Typography, Box, Skeleton, Divider,
} from "@mui/material";
import { ChevronLeft, Edit, Favorite, Pets, SmokingRooms } from "@mui/icons-material";
import { MainLoggedInLayout } from "../../../components/layouts/main.loggedIn.layout";
import { PostList } from "../../../components/post/PostList";
import { FollowAccountManager, MessageUser } from "../../../components/account/AccountActions";
import { dating_goals, gender, pronouns, relationship_style, removeHTMLtags, sexual_identity } from "../../../util/util";
import { ImageSwiper } from "../../../components/image.swiper";
import { useNavigate } from "react-router-dom";

const PRIDE = ['#E40303', '#FF8C00', '#FFED00', '#008026', '#24408E', '#732982']

const SectionLabel = ({ label, color = '#732982' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Box sx={{ width: 4, height: 16, borderRadius: 2, background: color, flexShrink: 0 }} />
    <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#23232b', letterSpacing: 0.2, textTransform: 'uppercase' }}>
      {label}
    </Typography>
  </Box>
)

const ProfileSkeleton = () => (
  <Box>
    {/* Hero */}
    <Skeleton variant="rectangular" width="100%" sx={{ height: { xs: 320, sm: 400 }, borderRadius: 3, mb: 2 }} />
    {/* Name */}
    <Box sx={{ px: 0.5 }}>
      <Skeleton variant="text" width="55%" height={34} sx={{ mb: 0.5 }} />
      <Skeleton variant="rounded" width={120} height={26} sx={{ mb: 2, borderRadius: 50 }} />
      {/* About */}
      <Skeleton variant="text" width="90%" sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="75%" sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <Skeleton variant="rounded" width={100} height={34} sx={{ borderRadius: 50 }} />
        <Skeleton variant="rounded" width={100} height={34} sx={{ borderRadius: 50 }} />
      </Box>
      <Divider sx={{ mb: 2.5 }} />
      {/* Identity chips */}
      <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
        {[90, 110, 80, 130].map(w => <Skeleton key={w} variant="rounded" width={w} height={28} sx={{ borderRadius: 50 }} />)}
      </Box>
      {/* Lifestyle chips */}
      <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
        {[70, 95, 85, 110, 75].map(w => <Skeleton key={w} variant="rounded" width={w} height={28} sx={{ borderRadius: 50 }} />)}
      </Box>
    </Box>
  </Box>
)

export const ProfileDisplay = ({ userDetails, id, account, token, handleScroll, media, swipe_card }) => {
  const [showPosts, setShowPosts] = useState(false);
  const navigate = useNavigate();

  const content = (
    !account ? (
      <ProfileSkeleton />
    ) : (
      <Box>
        {showPosts && (
          <Button
            onClick={() => setShowPosts(false)}
            startIcon={<ChevronLeft />}
            sx={{ mb: 2, borderRadius: 50, textTransform: 'none', fontWeight: 700, color: '#732982' }}
          >
            Back to profile
          </Button>
        )}

        {showPosts ? (
          <PostList media={media} />
        ) : (
          <>
            {/* ── Hero image ───────────────────────────────── */}
            <Box sx={{
              position: 'relative',
              borderRadius: 3,
              overflow: 'hidden',
              mb: 2.5,
              height: { xs: 320, sm: 400 },
              boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
            }}>
              {/* Pride strip */}
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 4, zIndex: 10,
                background: 'linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)',
                pointerEvents: 'none',
              }} />

              <ImageSwiper
                imgs={[{ img: account?.avatar }, ...(resolveUser(account).photos ?? [])]}
                sx={{
                  height: '100%',
                  '& .swiper': { height: '100%', borderRadius: 0 },
                  '& .swiper-slide': { height: '100%' },
                  '& .swiper-slide img': { width: '100%', height: '100%', objectFit: 'cover' },
                  '& .swiper-pagination': { bottom: '90px', zIndex: 6 },
                  '& .swiper-pagination-bullet': {
                    backgroundColor: 'rgba(255,255,255,0.75)',
                    border: '1px solid rgba(0,0,0,0.2)',
                    '&.swiper-pagination-bullet-active': { backgroundColor: '#fff' },
                  },
                  '& .swiper-button-next, & .swiper-button-prev': {
                    color: '#fff', zIndex: 6, textShadow: '0 0 4px rgba(0,0,0,0.8)',
                    '&:after': { fontSize: 22 },
                  },
                }}
              />

              {/* Gradient overlay — pointerEvents none so Swiper touch/drag still works */}
              <Box sx={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)',
                zIndex: 5,
                pointerEvents: 'none',
              }} />

              {/* Name + handle overlay */}
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                p: { xs: 2, sm: 2.5 }, zIndex: 6,
              }}>
                <Typography variant="h5" fontWeight={900} sx={{
                  color: '#fff', mb: 0.75, lineHeight: 1.2,
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                }}>
                  {account?.display_name || account?.username}
                </Typography>
                {swipe_card ? (
                  <Button
                    onClick={() => navigate("/profile/" + account?.id)}
                    size="small"
                    sx={{
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(8px)',
                      color: '#fff',
                      borderRadius: 50,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: 12,
                      px: 1.5,
                      '&:hover': { background: 'rgba(255,255,255,0.32)' },
                    }}
                  >
                    View Full Profile →
                  </Button>
                ) : (
                  <Chip
                    label={"@" + account?.acct}
                    size="small"
                    sx={{
                      background: 'rgba(255,255,255,0.18)',
                      color: '#fff',
                      fontWeight: 700,
                      backdropFilter: 'blur(8px)',
                      fontSize: 12,
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* ── Below hero ───────────────────────────────── */}
            <Box sx={{ px: 0.5 }}>
              {/* About */}
              {account?.note && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.7, color: '#3a3a4a' }}>
                    {removeHTMLtags(account.note)}
                  </Typography>
                </Box>
              )}

              {/* Actions */}
              {userDetails?.id !== id ? (
                !swipe_card && (
                  <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                    <FollowAccountManager auth_id={userDetails?.id} id={id} token={token} />
                    <MessageUser account={account} />
                  </Box>
                )
              ) : (
                <Box sx={{ mb: 2.5 }}>
                  <Button
                    onClick={() => navigate("/profile/edit/" + id)}
                    startIcon={<Edit />}
                    variant="outlined"
                    sx={{
                      borderRadius: 50,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderColor: '#732982',
                      color: '#732982',
                      '&:hover': { borderColor: '#732982', background: 'rgba(115,41,130,0.06)' },
                    }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              )}

              <Divider sx={{ mb: 2.5 }} />

              {/* Identity */}
              <IdentitySection account={account} />

              {/* Lifestyle */}
              <LifestyleSection account={account} />

              {/* Posts */}
              {media?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Divider sx={{ mb: 2.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0 }}>
                    <SectionLabel label="Posts & Media" color="#24408E" />
                    <Button
                      size="small"
                      onClick={() => setShowPosts(true)}
                      sx={{ color: '#24408E', textTransform: 'none', fontWeight: 700, fontSize: 12, mb: 1.5 }}
                    >
                      View {media.length} posts →
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    )
  )

  if (swipe_card) return content

  return (
    <MainLoggedInLayout>
      <Box
        onScroll={(e) => handleScroll(e)}
        sx={{ overflowY: 'auto', maxWidth: 680, mx: 'auto' }}
      >
        {content}
      </Box>
    </MainLoggedInLayout>
  )
}

const IdentitySection = ({ account }) => {
  const user = resolveUser(account)
  const basic_labels = [
    { label: user.pronouns?.map(i => pronouns[i]) ?? [] },
    { label: user.gender_identity?.map(i => gender[i]) ?? [] },
    { label: user.sexual_identity?.map(i => sexual_identity[i]) ?? [] },
    { label: user.relationship_style?.map(i => relationship_style[i]) ?? [] },
    { label: user.dating_goals?.map(i => dating_goals[i]) ?? [] },
  ].filter(x => x.label?.length > 0)

  if (basic_labels.length === 0) return null
  return (
    <Box sx={{ mb: 2.5 }}>
      <SectionLabel label="Identity" color="#732982" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {basic_labels.map((item, i) => (
          <Chip
            key={i}
            label={item.label.join(", ")}
            size="small"
            sx={{ borderRadius: 50, background: '#f3f0ff', color: '#732982', fontWeight: 600, border: '1px solid #e0d8f5' }}
          />
        ))}
      </Box>
    </Box>
  )
}

const LifestyleSection = ({ account }) => {
  const user = resolveUser(account)
  const lifestyle_labels = [
    { label: [...(user.relationship_status ?? "")], icon: <Favorite sx={{ fontSize: 13 }} /> },
    { label: [...(user.drinking ?? "")], icon: "🍷" },
    { label: [...(user.smoking ?? "")], icon: <SmokingRooms sx={{ fontSize: 13 }} /> },
    { label: [...(user.weed ?? "")], icon: "🌿" },
    { label: [...(user.political_views ?? "")], icon: "⚖️" },
    { label: [...(user.religion ?? "")], icon: "🙏" },
    { label: [...(user.diet ?? "")], icon: "🍽️" },
    { label: [...(user.star_sign ?? "")], icon: "💫" },
    { label: [...(user.pets ?? "")], icon: <Pets sx={{ fontSize: 13 }} /> },
    { label: [...(user.family_preference ?? "")], icon: "🍼" },
    { label: [...(user.intimacy_preferences ?? "")], icon: "💗" },
  ].filter(x => x.label?.length > 0 && x.label[0] !== "")

  if (lifestyle_labels.length === 0) return null
  return (
    <Box sx={{ mb: 2.5 }}>
      <SectionLabel label="Lifestyle" color="#E40303" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {lifestyle_labels.map((item, i) => (
          <Chip
            key={i}
            size="small"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>
                <span>{item.label.join(", ")}</span>
              </Box>
            }
            sx={{ borderRadius: 50, background: '#fff3f0', color: '#c0392b', fontWeight: 600, border: '1px solid #f5d8d5' }}
          />
        ))}
      </Box>
    </Box>
  )
}

function resolveUser(account) {
  const raw = account?.user
  if (Array.isArray(raw) && raw.length === 1) return raw[0]
  if (raw && !Array.isArray(raw)) return raw
  return {
    pronouns: [], gender_identity: [], sexual_identity: [],
    relationship_status: [], relationship_style: [], dating_goals: [],
    drinking: [], smoking: [], weed: [], political_views: [],
    religion: [], diet: [], star_sign: [], pets: [],
    family_preference: [], intimacy_preferences: [], photos: [],
  }
}
